import { baseUrl, requestHeadersBasicAuth, requestHeadersJsonBasicAuth } from './general-api';
import { v4 as generateUUID } from 'uuid';
import { HamiltonResourceBaseType, HamiltonResourceDocument, HamiltonResourceProduct } from './search-api';

/**
 * Base URL for the favorites API.
 */
export const baseFavoritesPath = baseUrl + '/.rest/nodes/v1/userdata/resources';

/**
 * Base URL to retrieve all resource center favorites.
 */
export const baseFavoritesGetAllPath = baseUrl + '/.rest/delivery/v1/resources/favorites';

/**
 * Specific properties that only resource favorites have.
 */
export interface FavoriteId {
  /**
   * This is the ID of the resource that is only used in the favorites list. When
   * adding a resource to the favorites, we generate a UUID for that favorite list entry.
   * The favoritesItemId is that UUID, that is needed to delete the item again. This is
   * not the ID of the resource (which is the nodeId).
   */
  favoritesItemId: string;
}

/**
 * Combined type for the search result
 */
export type HamiltonResourceFavorite = (HamiltonResourceProduct | HamiltonResourceDocument) & FavoriteId;

/**
 * Returns all favorites
 */
export const getAllFavorites = (): Promise<HamiltonResourceFavorite[]> => {
  return new Promise((resolve, reject) => {
    const url = baseFavoritesGetAllPath;

    if (document.querySelector('html').classList.contains('user-logged-out')) {
      return null;
    }
    return fetch(url, {
      headers: requestHeadersBasicAuth
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.results) {
          const favorites = data.results;
          resolve(favorites);
        }

        resolve([]);
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 * Adds the resource with the given id to the resource favorites.
 * @param resourceId: Id of the resource that should be added to the favorites.
 * @param resourceType: Type of the resource to add.
 * @returns Promise<string> which passes the new generated favoritesItemId for the favorites item.
 */
export const addItemToFavorites = (resourceId: string, resourceType: HamiltonResourceBaseType): Promise<string> => {
  return new Promise((resolve, reject) => {
    const uniqueResourceId = generateUUID();

    const updateFavoritesBody = {
      name: uniqueResourceId,
      type: 'mgnl:contentNode',
      properties: [
        {
          name: 'id',
          type: 'String',
          multiple: false,
          values: [resourceId]
        },
        {
          name: 'type',
          multiple: false,
          type: 'String',
          values: [resourceType]
        }
      ]
    };

    fetch(`${baseFavoritesPath} `, {
      method: 'PUT',
      headers: requestHeadersJsonBasicAuth,
      body: JSON.stringify(updateFavoritesBody)
    })
      .then((response) => {
        if (response.ok) {
          resolve(uniqueResourceId);
        } else {
          reject();
        }
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 * Removes the resource with the given id from favorites.
 * @param resourceId: Id of the resource that should be removed from the favorites list. Bear in mind that this is not the
 * resource ID but rather a unique, generated one that is only used for the favorites list.
 */
export const deleteItemFromFavoritesList = (resourceId: string): Promise<boolean> => {
  if (resourceId) {
    return fetch(`${baseFavoritesPath}/${resourceId}`, {
      method: 'DELETE',
      headers: requestHeadersJsonBasicAuth
    })
      .then((response) => {
        return response.ok;
      })
      .catch(() => {
        return false;
      });
  }

  return Promise.resolve(false);
};
